import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { NEW_PAYMENT_CREATION_STEPS } from 'components/common/constants';
import SelectAccountStep from './components/SelectAccountStep';
import AccountDetailsStep from './components/AccountDetailsStep';
import BankDetailsStep from './components/BankDetailsStep';
import PaymentDetailsStep from './components/PaymentDetailsStep';
import RecipientAdditionalDetailsStep from './components/RecipientAdditionalDetailsStep';
import RecipientDetailsStep from './components/RecipientDetailsStep';
import ReviewAndSaveNewPaymentFormStep from './components/ReviewAndSaveNewPaymentFormStep';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert, isCryptoProvider } from 'services/utils';
import Alert from 'uikit/Alert/Alert';
import CustomFormSlider from 'uikit/CustomFormSlider/CustomFormStepper';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import './CreateNewPaymentFormPage.scss';

const CreateNewPaymentFormPage = ({ customerNumber, userAccounts, newPaymentStore }) => {
  const i18n = useContext(i18nContext);
  const [headerText, setHeaderText] = useState(i18n.getMessage('container.newNewPaymentForm'));

  const renderCurrentStep = () => {
    const currentStep = newPaymentStore.getCurrentStep();

    switch (currentStep) {
      case NEW_PAYMENT_CREATION_STEPS.SELECT_ACCOUNT:
        return <SelectAccountStep userAccounts={userAccounts} />;
      case NEW_PAYMENT_CREATION_STEPS.ACCOUNT_DETAILS:
        return <AccountDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case NEW_PAYMENT_CREATION_STEPS.BANK_DETAILS:
        return <BankDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case NEW_PAYMENT_CREATION_STEPS.RECIPIENT_DETAILS:
        return <RecipientDetailsStep customerNumber={customerNumber} />;
      case NEW_PAYMENT_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS:
        return <RecipientAdditionalDetailsStep customerNumber={customerNumber} userAccounts={userAccounts} />;
      case NEW_PAYMENT_CREATION_STEPS.PAYMENT_DETAILS:
        return <PaymentDetailsStep customerNumber={customerNumber} />;
      case NEW_PAYMENT_CREATION_STEPS.REVIEW_AND_SAVE_NEW_PAYMENT_FORM:
        return <ReviewAndSaveNewPaymentFormStep customerNumber={customerNumber} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const currentStep = newPaymentStore.getCurrentStep();

    if (currentStep === NEW_PAYMENT_CREATION_STEPS.SELECT_ACCOUNT) {
      setHeaderText(i18n.getMessage('container.selectAccount'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.ACCOUNT_DETAILS) {
      setHeaderText(i18n.getMessage('container.accountDetails'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.BANK_DETAILS) {
      setHeaderText(i18n.getMessage('container.bankDetails'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.RECIPIENT_DETAILS) {
      setHeaderText(i18n.getMessage('container.recipientDetails'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.RECIPIENT_ADDITIONAL_DETAILS) {
      setHeaderText(i18n.getMessage('container.recipientAdditionalDetails'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.PAYMENT_DETAILS) {
      setHeaderText(i18n.getMessage('container.paymentDetails'));
    } else if (currentStep === NEW_PAYMENT_CREATION_STEPS.REVIEW_AND_SAVE_NEW_PAYMENT_FORM) {
      setHeaderText(i18n.getMessage('container.reviewAndCheckNewPayment'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPaymentStore.currentStepIndex]);

  return (
    <NewContainer
      className={'content-card-wrapper'}
      header={headerText}
      isBackButton={newPaymentStore.getCurrentStep() !== NEW_PAYMENT_CREATION_STEPS.SELECT_ACCOUNT}
      handleBackButtonClick={() => newPaymentStore.goToPrevStep()}
    >
      {newPaymentStore.getCurrentStep() !== NEW_PAYMENT_CREATION_STEPS.SELECT_ACCOUNT && (
        <CustomFormSlider
          className={'new-payment-create-new-payment-form-page-stepper'}
          steps={newPaymentStore.newPaymentFormSteps}
          currentStep={newPaymentStore.getCurrentStepIndex()}
        />
      )}

      <div className={'new-payment-content-wrapper'}>
        <Alert
          className={`new-payment-alert ${!isCryptoProvider(newPaymentStore.currentAccount?.transfer_provider) ? 'new-payment-alert-with-attachment' : ''}`}
          type={'warning'}
          message={
            newPaymentStore.error && !newPaymentStore.error?.type
              ? getErrorMessageForAlert(
                  i18n,
                  newPaymentStore.error,
                  true,
                  newPaymentStore.paymentDetailsStep?.paymentMethod
                )
              : ''
          }
        />

        {renderCurrentStep()}
      </div>
    </NewContainer>
  );
};

CreateNewPaymentFormPage.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore.userAccounts,
  newPaymentStore: stores.newPaymentStore
}))(observer(CreateNewPaymentFormPage));
